import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  const openPost = async (slug) => {
    setLoading(true);
    try {
      const r = await api.get(`/blog/${slug}`);
      setActive(r.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="relative py-24 sm:py-32 bg-parchment-alt dark:bg-[#0B0F19]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <Overline>Journal, Press & Reflections</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Ideas worth returning to.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Writing on faith, revival, leadership, and legacy — published to
              inform, encourage, and build up.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <motion.button
                data-testid={`blog-card-${p.slug}`}
                onClick={() => openPost(p.slug)}
                whileHover={{ y: -8 }}
                className="group text-left h-full w-full flex flex-col p-7 rounded-2xl border border-border bg-card transition-shadow hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold-deep dark:text-gold-light">
                    {p.category}
                  </span>
                  <ArrowUpRight
                    size={18}
                    className="text-muted-foreground group-hover:text-gold-deep dark:group-hover:text-gold-light group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                  />
                </div>
                <h3 className="mt-4 font-serif text-2xl font-semibold leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">
                  {p.excerpt}
                </p>
                <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground font-mono">
                  <span>{formatDate(p.date)}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} /> {p.read_time}
                  </span>
                </div>
              </motion.button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="blog-reader-dialog"
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          {active && (
            <>
              <DialogHeader>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold-deep dark:text-gold-light">
                  {active.category}
                </span>
                <DialogTitle className="font-serif text-3xl font-semibold leading-tight text-left">
                  {active.title}
                </DialogTitle>
                <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono pt-1">
                  <span>{formatDate(active.date)}</span>
                  <span>{active.read_time}</span>
                </div>
              </DialogHeader>
              <article className="mt-2 space-y-4 text-base leading-relaxed text-foreground/90">
                {active.body.map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </article>
            </>
          )}
          {loading && !active && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
